import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { X, Sparkles, Gift, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Coach/Consultant",
  "Creative/Designer",
  "Health & Wellness",
  "E-commerce/Retail",
  "Service Provider",
  "Content Creator",
  "Tech/SaaS",
  "Education/Training",
  "Other"
];

const LeadCaptureModal = ({ isOpen, onClose, source = "free_tool", onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business_type: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads/capture`, {
        ...formData,
        source
      });
      
      if (response.data.already_captured) {
        toast.success('Welcome back!');
      } else {
        toast.success('Welcome to Clierxter!');
      }
      
      // Store in localStorage to not show again
      localStorage.setItem('lead_captured', 'true');
      localStorage.setItem('lead_email', formData.email);
      
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="relative w-full max-w-md overflow-hidden"
        >
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-[#52525B] hover:text-white z-10 transition-colors"
          >
            <X size={20} />
          </button>

          {/* Background gradient */}
          <div className="absolute inset-0 bg-[#0A0A0A]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/10 to-transparent rounded-full blur-3xl" />
          
          <div className="relative p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-[#D4AF37]/20 to-[#E5C585]/20 border border-[#D4AF37]/30">
                <Gift className="text-[#D4AF37]" size={28} />
              </div>
              
              <p className="text-[#D4AF37] text-[10px] font-accent tracking-[0.2em] mb-2">FREE ACCESS</p>
              <h2 className="font-serif text-2xl text-white mb-2">
                Unlock Your Free Brand Tools
              </h2>
              <p className="text-[#A1A1AA] text-sm font-light">
                Enter your details to access free brand-building tools and receive personalized tips.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Name"
                  className="bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] rounded-none px-0 py-3 text-white placeholder:text-[#52525B]"
                  data-testid="lead-name"
                />
              </div>
              
              <div>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Your Email"
                  className="bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] rounded-none px-0 py-3 text-white placeholder:text-[#52525B]"
                  data-testid="lead-email"
                />
              </div>
              
              <div>
                <select
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] py-3 text-white outline-none appearance-none"
                  data-testid="lead-business-type"
                >
                  <option value="" className="bg-[#0A0A0A]">Business Type (Optional)</option>
                  {BUSINESS_TYPES.map(type => (
                    <option key={type} value={type} className="bg-[#0A0A0A]">{type}</option>
                  ))}
                </select>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="btn-gold w-full py-4 mt-6 flex items-center justify-center gap-2"
                data-testid="lead-submit"
              >
                {loading ? 'Processing...' : (
                  <>
                    <Sparkles size={18} />
                    Get Free Access
                  </>
                )}
              </Button>
            </form>

            {/* Benefits */}
            <div className="mt-8 pt-6 border-t border-[#1C1C1C]">
              <p className="text-[#52525B] text-xs text-center mb-3">What you'll get:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {['Free Brand Audit', 'Content Ideas', 'Brand Tips', 'Community Access'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-[#A1A1AA]">
                    <div className="w-1 h-1 bg-[#D4AF37]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Privacy note */}
            <p className="text-[#52525B] text-[10px] text-center mt-6">
              We respect your privacy. No spam, unsubscribe anytime.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadCaptureModal;
